import React from 'react'
import { Card, Stack, Group, Text, Badge, ActionIcon, Tooltip } from '@mantine/core'
import { IconCopy, IconExternalLink, IconCheck, IconX } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../../components/ThemeProvider'
import { IsaacTemplateMerchantResVO } from '../../typings/promotionTypes'
import { notifications } from '@mantine/notifications'
import { isPC } from '../../utils/deviceUtil'

interface ReportCardProps {
  report: IsaacTemplateMerchantResVO
}

export default function ReportCard({ report }: ReportCardProps) {
  const navigate = useNavigate()
  const theme = useTheme()
  const isPCDevice = isPC()
  const [isHovered, setIsHovered] = React.useState(false)

  const getTemplateTypeText = (type: number) => {
    return type === 1 ? '个人报告' : type === 2 ? '企业报告' : '未知类型'
  }

  const getTemplateTypeBadgeColor = (type: number) => {
    return type === 1 ? 'blue' : type === 2 ? 'green' : 'gray'
  }

  const copyLinkToClipboard = (e: React.MouseEvent) => {
    e.stopPropagation()
    const link = `${window.location.origin}/queryReport?linkId=${report.id}`
    navigator.clipboard
      .writeText(link)
      .then(() => {
        notifications.show({
          message: '链接已复制',
          color: 'green',
          icon: <IconCheck size={18} />,
        })
      })
      .catch(() => {
        notifications.show({
          message: '复制失败',
          color: 'red',
          icon: <IconX size={18} />,
        })
      })
  }

  const handleCardClick = () => {
    navigate(`/queryReport?linkId=${report.id}`)
  }

  return (
    <Card
      shadow="sm"
      padding={isPCDevice ? 'lg' : 'md'}
      radius="lg"
      style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f9fafc 100%)',
        border: `1px solid ${theme.surfaceBorderColor}`,
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 12px 24px rgba(0,0,0,0.08)' : undefined,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
    >
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Text
            fw={600}
            size={isPCDevice ? 'md' : 'sm'}
            style={{
              color: theme.primaryColor,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {report.templateName}
          </Text>
          <Badge color={getTemplateTypeBadgeColor(report.templateType)} size={isPCDevice ? 'md' : 'sm'}>
            {getTemplateTypeText(report.templateType)}
          </Badge>
        </Group>

        <Tooltip label={`授权ID: ${report.id}`} withArrow>
          <Badge variant="dot" color="blue" size="sm">
            ID: {report.id.substring(0, 8)}...
          </Badge>
        </Tooltip>

        <Group justify="space-between" align="center">
          <Tooltip label="复制链接" withArrow>
            <ActionIcon
              variant="subtle"
              color={theme.primaryColor}
              onClick={copyLinkToClipboard}
              size={isPCDevice ? 'md' : 'sm'}
            >
              <IconCopy size={18} />
            </ActionIcon>
          </Tooltip>

          <Group gap={4} style={{ color: theme.primaryColor }}>
            <Text size="xs" fw={500} c={theme.primaryColor}>
              查看详情
            </Text>
            <IconExternalLink size={14} />
          </Group>
        </Group>
      </Stack>
    </Card>
  )
}
